import { invalidate, useFrame } from '@react-three/fiber';
import { Line, LineProps } from '@react-three/drei';
import { useEffect, useMemo, useRef } from 'react';
import { Vector2, Vector3 } from 'three';
import { Line2, LineMaterial } from 'three-stdlib';

import fragmentShader from './LineFragment.glsl';
import vertexShader from './LineVertex.glsl';

interface AnimatedLineProps extends LineProps {
  velocity: number;
  enabled?: boolean; // If false, animation will not run
}

const tempVec1 = new Vector3();
const tempVec2 = new Vector3();

const getNormalizedPoint = (point: LineProps['points'][0], target: Vector3): Vector3 => {
  if (point instanceof Vector3) {
    return target.copy(point);
  }
  if (point instanceof Vector2) {
    return target.set(point.x, point.y, 0);
  }
  if (typeof point === 'number') {
    return target.set(point, 0, 0);
  }
  return target.set(point[0], point[1], point[2] ?? 0);
};

const setDefaultUniforms = (material: LineMaterial) => {
  if (!material.uniforms.uTime) {
    material.uniforms.uTime = { value: 0 };
    material.uniforms.uDistance = { value: 0 };
    material.uniforms.uStartPoint = { value: new Vector3() };
    material.uniforms.uEndPoint = { value: new Vector3() };
    material.uniforms.uAngle = { value: 0 };
  }
};

const AnimatedLine = (props: AnimatedLineProps) => {
  const { points, velocity, enabled = true, ...rest } = props;

  const ref = useRef<Line2>(null);
  const timeRef = useRef(0);

  const { distance, angle } = useMemo(() => {
    let fullDistance = 0;
    for (let i = 1; i < points.length; i++) {
      getNormalizedPoint(points[i - 1], tempVec1);
      getNormalizedPoint(points[i], tempVec2);
      fullDistance += tempVec2.sub(tempVec1).length();
    }

    let angle = 0;
    if (points.length > 2) {
      getNormalizedPoint(points[0], tempVec1);
      getNormalizedPoint(points[1], tempVec2);
      tempVec1.sub(tempVec2);

      getNormalizedPoint(points[2], tempVec2);
      const p1 = getNormalizedPoint(points[1], new Vector3());
      tempVec2.sub(p1);

      const lengths = tempVec1.length() * tempVec2.length();
      if (lengths > 0) {
        const dotProduct = tempVec1.dot(tempVec2);
        angle = Math.acos(Math.min(1, Math.max(-1, dotProduct / lengths)));
      }
    }

    return { distance: fullDistance, angle };
  }, [points]);

  // start/stop animation
  useEffect(invalidate, [enabled]);

  useFrame(({ clock}) => {
    const line = ref.current;
    if (!line) return;

    setDefaultUniforms(line.material);
    
    if (enabled) {
      const currentElapsed = clock.getElapsedTime();
      const delta = currentElapsed - timeRef.current;
      line.material.uniforms.uTime.value += delta * velocity;
      timeRef.current = currentElapsed;
      invalidate();
    }
    
  });

  useEffect(() => {
    const line = ref.current;
    if (!line) return;

    setDefaultUniforms(line.material);

    const uniforms = line.material.uniforms;
    uniforms.uDistance.value = distance;
    uniforms.uAngle.value = angle;
    getNormalizedPoint(points[0], uniforms.uStartPoint.value as Vector3);
    getNormalizedPoint(points[points.length - 1], uniforms.uEndPoint.value as Vector3);
  }, [angle, distance, points]);

  return <Line fragmentShader={fragmentShader} points={points} ref={ref} vertexShader={vertexShader} {...rest} />;
};

export default AnimatedLine;
